import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaceTabs } from '@uv-inserts/components/FaceTabs';
import {
  Visualiser,
  VisualiserGroup,
  VisualiserTextBox,
  VisualiserImageArea,
  VisualiserTextList
} from '@uv-inserts/components/Visualiser';
import PersonalisationSlotHeader from '../../PersonalisationSlotHeader';
import { FileInput } from '../../FileInput';
import fileInputStyles from '../../FileInput/file-input.module.css';
import { ToggleInput } from '../../ToggleInput';
import { usePersonalisationContext } from '../../../context/PersonalisationContext';
import { useLocale } from '../../../context/LocaleContext';
import {
  usePersonalisationUvInsertSlotViewModel,
  resolveAllowedUvInsertConfigs,
  UV_INSERT_CARD_SLIDE_LABEL
} from './PersonalisationUvInsertSlot.vm';
import { deriveStoryCardText, findUvInsertTextListElement } from '../../../utilities/deriveStoryCardText';
import { getZeroPriceLabel } from '../../../utilities/getZeroPriceLabel';
import { getStepSkipButtonText } from '../../PersonalisationFooter/useViewModel';
import { buildUvInsertTextVisualiserSize } from '@shared/view/uv-inserts/build-text-visualiser-size';
import { getUvInsertPriceRange } from '@shared/view/uv-inserts/get-price-display';
import { UvInsertFields, getFieldElements } from './UvInsertFields';
import styles from './personalisation-uv-insert-slot.module.css';

/**
 * Converts a number or string dimension value to a CSS string.
 * Required because UVInsertConfig stores sizing as numbers (e.g. 200) or strings (e.g. "100%").
 * Always returns a string — falls back to the provided fallback or an empty string.
 *
 * @param {string|number|undefined} value - The value to convert.
 * @param {string} [fallback=''] - Fallback string if value is undefined.
 * @returns {string} The CSS string value.
 */
function toCssDimension(value, fallback = '') {
  if (value === undefined) return fallback;
  return String(value);
}

/** Stable empty map so a config without a TEXT_LIST never re-triggers the VM's merge effect. */
const EMPTY_DERIVED_TEXT = {};

/**
 * Text the card derives from other slots rather than the customer typing it.
 *
 * Only configs with a TEXT_LIST element (the Story of Us keepsake card) have any, and it is
 * computed from context values only — no new context state — so it re-derives whenever the
 * PAIRED step's stones, years or moments change — all three are derived from the slots the
 * customer fills in under each stone on the stones step; nothing on the card edits them.
 *
 * A `null` derive (no PAIRED step to read) is treated as empty here: this hook only seeds the
 * mounted editor's own keys, and the context effect is what protects stored text from the
 * orphan rule when the stones step is hidden.
 *
 * @param {Object} options
 * @param {import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts|null} options.resolvedUvInserts
 * @param {string|undefined} options.configHandle - The config being edited.
 * @returns {Record<string, string>} The derived `textByKey` slice, empty when nothing is derived.
 */
function useDerivedStoryCardText({ resolvedUvInserts, configHandle }) {
  const { personalisationSteps, personalisationSlots, selections, variants } = usePersonalisationContext();

  const activeTextListElement = useMemo(() => {
    const config = resolvedUvInserts?.configs?.find((c) => c.handle === configHandle) ?? null;
    return findUvInsertTextListElement({ config });
  }, [resolvedUvInserts, configHandle]);

  return useMemo(
    () =>
      (activeTextListElement
        ? deriveStoryCardText({
            steps: personalisationSteps,
            slots: personalisationSlots,
            selections: selections?.slots ?? [],
            variants,
            element: activeTextListElement
          })
        : null) ?? EMPTY_DERIVED_TEXT,
    [activeTextListElement, personalisationSteps, personalisationSlots, selections, variants]
  );
}

/**
 * Shown under the photo upload when the framed photo could not be cropped. Neither the Figma nor
 * the brief has copy for this; it lives here alone so it is cheap to change.
 */
const CROP_FAILED_MESSAGE = "We couldn't prepare your photo. Please try again or choose a different photo.";

/**
 * Tells the footer while this slot's framed photo is not ready for the cart.
 *
 * Until the crop lands the slot's selection carries the customer's uncropped upload, so an add to
 * bag in that window would print the photo they did not frame (the keepsake card race, QA
 * 2026-10-09). A crop that failed holds too: the selection then carries no photo, until the
 * customer re-frames or replaces it and that crop lands. The slot id stays in the context's list
 * from the render the framing changed in until then, and leaves it on unmount. An opted-out insert
 * sends no image, so it never holds the footer.
 *
 * @param {Object} options
 * @param {string} options.slotId - The UV insert slot.
 * @param {ReturnType<typeof usePersonalisationUvInsertSlotViewModel>} options.vm - The slot's view model.
 */
function usePublishCropPending({ slotId, vm }) {
  const { setCropPendingSlotIds } = usePersonalisationContext();
  const isCropPending = vm.optedIn && (vm.isCropPending || vm.cropFailedKeys.length > 0);

  useEffect(() => {
    if (!isCropPending) return undefined;
    setCropPendingSlotIds((ids) => [...ids, slotId]);
    return () => setCropPendingSlotIds((ids) => ids.filter((id) => id !== slotId));
  }, [isCropPending, slotId, setCropPendingSlotIds]);
}

/**
 * Renders the UV insert editor UI (face tabs, visualiser, file upload).
 * Shared between single-config and multi-config paths — the caller is responsible
 * for opt-in gating and toggle display.
 *
 * @param {Object} props
 * @param {ReturnType<typeof usePersonalisationUvInsertSlotViewModel>} props.vm
 * @param {Object} props.slot
 */
function UvInsertEditorBody({ vm, slot }) {
  return (
    <div className={styles['personalisation__uv-insert-container']}>
      {vm.faceTabItems.length > 1 && (
        <FaceTabs
          tabs={vm.faceTabItems}
          activeIndex={vm.activeTabIndex}
          onActiveIndexChange={vm.setActiveTabIndex}
          fullWidth
        />
      )}

      <div
        role="tabpanel"
        id={`uv-insert-panel-${slot.id}-${vm.activeTabIndex}`}
        aria-labelledby={`uv-insert-tab-${slot.id}-${vm.activeTabIndex}`}
        className={styles['personalisation__uv-insert-tab-panel']}
      >
        <VisualiserGroup>
          {vm.facesToShow.map((face, fi) => {
            const frontThemeBackground =
              face.location === 'FRONT' && vm.selectedThemeImageSrc ? vm.selectedThemeImageSrc : null;

            return (
              <Visualiser
                key={`${face.location}-${fi}`}
                type={face.type}
                background={face.backgroundColor}
                backgroundImageUrl={frontThemeBackground ?? undefined}
                subtitle={face.subtitle}
              >
                {face.elementConfig.map((el, ei) => {
                  if (el.type === 'TEXT') {
                    const key = (el.lineItemPropertyKey ?? '').trim();
                    if (el.editable === false) {
                      if (!key) {
                        return (
                          <VisualiserTextBox
                            key={`static-${face.location}-${fi}-${ei}`}
                            font={el.font}
                            fontWeight={el.fontWeight}
                            fontSize={el.fontSize}
                            minFontSize={el.minFontSize}
                            lineHeight={el.lineHeight}
                            color={el.color}
                            maxLength={el.maxLength}
                            maxLines={el.maxLines}
                            size={buildUvInsertTextVisualiserSize(el.size)}
                            position={{
                              top: toCssDimension(el.positioning?.top),
                              left: toCssDimension(el.positioning?.left),
                              right: toCssDimension(el.positioning?.right),
                              bottom: toCssDimension(el.positioning?.bottom)
                            }}
                            style={el.style}
                            userEditable={false}
                            value={el.value ?? ''}
                            setValue={() => {}}
                          />
                        );
                      }
                      const storedReadOnly = vm.textByKey[key];
                      const readOnlyDisplay =
                        storedReadOnly != null && storedReadOnly !== '' ? storedReadOnly : (el.value ?? '');
                      return (
                        <VisualiserTextBox
                          key={`${key}-${ei}`}
                          font={el.font}
                          fontWeight={el.fontWeight}
                          fontSize={el.fontSize}
                          minFontSize={el.minFontSize}
                          lineHeight={el.lineHeight}
                          color={el.color}
                          maxLength={el.maxLength}
                          maxLines={el.maxLines}
                          size={buildUvInsertTextVisualiserSize(el.size)}
                          position={{
                            top: toCssDimension(el.positioning?.top),
                            left: toCssDimension(el.positioning?.left),
                            right: toCssDimension(el.positioning?.right),
                            bottom: toCssDimension(el.positioning?.bottom)
                          }}
                          style={el.style}
                          userEditable={false}
                          value={readOnlyDisplay}
                          setValue={(v) => vm.setText(key, v)}
                        />
                      );
                    }
                    return (
                      <VisualiserTextBox
                        key={`${key}-${ei}`}
                        font={el.font}
                        fontWeight={el.fontWeight}
                        fontSize={el.fontSize}
                        minFontSize={el.minFontSize}
                        lineHeight={el.lineHeight}
                        color={el.color}
                        maxLength={el.maxLength}
                        maxLines={el.maxLines}
                        maxCharsPerLine={el.maxCharsPerLine}
                        size={buildUvInsertTextVisualiserSize(el.size)}
                        position={{
                          top: toCssDimension(el.positioning?.top),
                          left: toCssDimension(el.positioning?.left),
                          right: toCssDimension(el.positioning?.right),
                          bottom: toCssDimension(el.positioning?.bottom)
                        }}
                        style={el.style}
                        // The config's `value` is the caption's default prompt; undefined keeps
                        // VisualiserTextBox's own "Tap to edit" fallback for configs without one.
                        initialValue={el.value || undefined}
                        // The face box shares its field's label, so screen readers name both alike (AC9).
                        ariaLabel={el.inputLabel?.trim() || undefined}
                        value={vm.textByKey[key] ?? ''}
                        setValue={(v) => vm.setText(key, v)}
                      />
                    );
                  }

                  if (el.type === 'IMAGE_UPLOAD') {
                    const key = el.lineItemPropertyKey;
                    const file = vm.imageByKey[key] ?? null;
                    const transform = vm.imageTransformByKey[key] ?? { scale: 1, x: 0, y: 0 };
                    return (
                      <VisualiserImageArea
                        key={`${key}-${ei}`}
                        file={file}
                        backgroundColor={el.backgroundColor}
                        size={{
                          width: toCssDimension(el.size?.width, '200px'),
                          height: toCssDimension(el.size?.height, '200px')
                        }}
                        position={{
                          top: toCssDimension(el.positioning?.top),
                          left: toCssDimension(el.positioning?.left)
                        }}
                        borderRadius={el.borderRadius}
                        style={el.style}
                        scale={transform.scale}
                        x={transform.x}
                        y={transform.y}
                        onUpdate={(updates) => {
                          vm.setImageTransformByKey((prev) => ({
                            ...prev,
                            [key]: {
                              scale: updates.scale ?? transform.scale,
                              x: updates.x ?? transform.x,
                              y: updates.y ?? transform.y
                            }
                          }));
                        }}
                      />
                    );
                  }

                  if (el.type === 'TEXT_LIST') {
                    return (
                      <VisualiserTextList
                        key={`text-list-${face.location}-${fi}-${ei}`}
                        element={el}
                        textByKey={vm.textByKey}
                      />
                    );
                  }

                  return null;
                })}
              </Visualiser>
            );
          })}
        </VisualiserGroup>
      </div>
    </div>
  );
}

/**
 * The photo upload control(s) for the active face.
 *
 * Deliberately outside `UvInsertEditorBody`: when the face is rendered as a slide in the piece
 * visualiser the editor is portalled into the carousel, and the upload button has to stay in the
 * step body underneath it, where the customer can reach it while scrolling the step.
 *
 * @param {Object} props
 * @param {ReturnType<typeof usePersonalisationUvInsertSlotViewModel>} props.vm
 * @param {Object} props.slot
 */
function UvInsertPhotoUploads({ vm, slot }) {
  // Keys the config gives a labelled field below the face are uploaded there instead.
  const fieldKeys = new Set(getFieldElements(vm.facesToShow).map((element) => element.lineItemPropertyKey.trim()));
  const uploadKeys = vm.imageUploadKeysForActiveTab.filter((imageKey) => !fieldKeys.has(imageKey));
  if (uploadKeys.length === 0) return null;

  return (
    <div className={styles['personalisation__uv-insert-file-upload-container']}>
      {uploadKeys.map((imageKey) => (
        <FileInput
          key={imageKey}
          label={uploadKeys.length === 1 ? 'Upload photo' : `Upload (${imageKey})`}
          onFileSelect={(value) => {
            const file = value.userImage !== null && value.userImage.file instanceof File ? value.userImage.file : null;
            vm.setImageByKey((prev) => ({ ...prev, [imageKey]: file }));
          }}
          accept="png,jpg,jpeg,webp"
          disabled={false}
          buttonVariant="primary"
          buttonColor="orange"
          buttonSize="medium"
          maxFileSize={5 * 1024 * 1024}
          id={`uv-insert-img-${slot.id}-${imageKey}`}
          selectedFile={vm.imageByKey[imageKey] ?? null}
        />
      ))}
      {/* Marked up and styled as FileInput's own upload error, under the button that fixes it. */}
      {uploadKeys.some((imageKey) => vm.cropFailedKeys.includes(imageKey)) && (
        <div className={fileInputStyles['file-input__error'] + ' body-sm font-medium'} role="alert">
          {CROP_FAILED_MESSAGE}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the editable face either inline in the step body (every insert, as always) or inside the
 * slide the piece visualiser keeps for it (the keepsake card).
 *
 * Mounted only while the customer is opted in, so unmounting is the opt-out path as well as the
 * leave-the-step path — both have to give the slide back.
 *
 * The portal is a portal rather than a re-render on purpose: the same React elements keep the same
 * state, so the tap-to-edit textareas, the polaroid's pinch/pan and the crop all behave in the
 * carousel exactly as they do inline. When no container exists — the visualiser is off
 * (`visEnabled !== 'true'`), or it has not rendered the slide yet — the face falls back inline.
 *
 * There is only ever one slide container, so only the slot the context currently holds may portal
 * into it: if a product ever allowed two card slots, the loser renders inline rather than stacking
 * both editors into the one slide.
 *
 * @param {Object} props
 * @param {ReturnType<typeof usePersonalisationUvInsertSlotViewModel>} props.vm
 * @param {Object} props.slot
 */
function UvInsertEditorPlacement({ vm, slot }) {
  const { setUvInsertSlide, uvInsertSlide, uvInsertSlideContainer } = usePersonalisationContext();
  const rendersInCarousel = vm.rendersInCarousel;

  useEffect(() => {
    if (!rendersInCarousel) {
      setUvInsertSlide((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setUvInsertSlide({ slotId: slot.id, label: UV_INSERT_CARD_SLIDE_LABEL });

    return () => {
      setUvInsertSlide((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [rendersInCarousel, setUvInsertSlide, slot.id]);

  const body = <UvInsertEditorBody vm={vm} slot={slot} />;

  if (rendersInCarousel && uvInsertSlide?.slotId === slot.id && uvInsertSlideContainer) {
    return createPortal(body, uvInsertSlideContainer);
  }

  return body;
}

/**
 * Routes UV insert slots by how many config handles admin allowed on this slot.
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @returns {import('react').JSX.Element|null}
 */
export default function PersonalisationUvInsertSlot({ slot }) {
  const { resolvedUvInserts } = usePersonalisationContext();
  const configHandles = slot.uvInsertConfigHandles ?? [];

  if (!resolvedUvInserts) return null;

  if (configHandles.length === 0) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config not found for this slot. Check the slot configuration in admin.</p>
      </div>
    );
  }

  if (configHandles.length === 1) {
    return <PersonalisationUvInsertSlotSingle slot={slot} configHandle={configHandles[0]} />;
  }

  return <PersonalisationUvInsertSlotMulti slot={slot} configHandles={configHandles} />;
}

/**
 * Multiple allowed UV insert types — inline chooser that lets the customer pick one config,
 * then renders the full editor for the chosen config.
 *
 * The selected configHandle is stored in the selection payload so pricing, validation, and
 * cart encoding all resolve against the chosen config rather than a slot-level default.
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @param {string[]} props.configHandles
 */
function PersonalisationUvInsertSlotMulti({ slot, configHandles }) {
  const { resolvedUvInserts, updateSelections, selections, setActiveUvInsertChooser } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  const allowedConfigs = useMemo(
    () => resolveAllowedUvInsertConfigs({ handles: configHandles, resolvedUvInserts }),
    [configHandles, resolvedUvInserts]
  );

  // --- Price range across all allowed configs, driven purely by variant prices ---
  const priceRange = useMemo(
    () =>
      getUvInsertPriceRange({
        configs: allowedConfigs,
        configHandles: allowedConfigs.map((c) => c.handle)
      }),
    [allowedConfigs]
  );

  // True when different configs have different prices — drives per-card price display.
  const pricesVary = priceRange !== null && priceRange.minPounds !== priceRange.maxPounds;

  // Per-config cheapest theme price in pounds, only computed when prices vary.
  const configMinPricePounds = useMemo(() => {
    if (!pricesVary) return new Map();
    const map = new Map();
    for (const config of allowedConfigs) {
      const prices = config.themes
        .map((t) => parseFloat(String(t.price)))
        .filter((p) => !Number.isNaN(p) && p > 0)
        .map((p) => p / 100);
      if (prices.length > 0) map.set(config.handle, Math.min(...prices));
    }
    return map;
  }, [pricesVary, allowedConfigs]);

  const togglePriceText = useMemo(() => {
    if (!priceRange) return null;
    if (pricesVary) {
      return `- From ${formatPrice({ amount: priceRange.minPounds })}`;
    }
    return `+${formatPrice({ amount: priceRange.minPounds })}`;
  }, [priceRange, pricesVary, formatPrice]);

  const existingSelection = /** @type {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} */ (
    selections?.slots.find((s) => s.id === slot.id)?.selected ?? null
  );

  // Restore the previously chosen config on back-navigation.
  const [chosenHandle, setChosenHandle] = useState(() => existingSelection?.configHandle ?? '');

  // Show the chooser list first; collapse to editor once the customer confirms a pick.
  // Skip straight to editor when restoring from back-navigation.
  const [showChooser, setShowChooser] = useState(() => existingSelection?.configHandle == null);

  const handleSelectionChange = useCallback(
    (selection) => {
      updateSelections({ slotId: slot.id, itemSelected: selection });
    },
    [slot.id, updateSelections]
  );

  const derivedTextByKey = useDerivedStoryCardText({ resolvedUvInserts, configHandle: chosenHandle });

  const vm = usePersonalisationUvInsertSlotViewModel({
    resolvedUvInserts,
    configHandle: chosenHandle,
    isRequired: slot.isRequired,
    uvInsertVisibleByDefault: slot.uvInsertVisibleByDefault,
    isFreeSlot: slot.isFreeSlot,
    overridePrice: slot.overridePrice,
    existingSelection,
    derivedTextByKey,
    onSelectionChange: handleSelectionChange
  });

  usePublishCropPending({ slotId: slot.id, vm });

  // Keep the footer chooser (themes/suggestions) in sync with the active config.
  // Suppress it while the type-selection chooser is visible — the strip should only
  // appear once the customer is on the editor page.
  useEffect(() => {
    if (!vm.optedIn || !vm.activeConfig || !resolvedUvInserts || showChooser) {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setActiveUvInsertChooser({
      slotId: slot.id,
      config: vm.activeConfig,
      tabIndex: vm.activeTabIndex,
      suggestedMessaging: resolvedUvInserts.suggestedMessaging,
      selectedThemeIndex: vm.themeIndex,
      onThemeIndexChange: vm.setThemeIndex,
      themeChooserOpen: vm.themeChooserOpen,
      onThemeChooserOpenChange: vm.setThemeChooserOpen,
      selectedSuggestionGroupIndex: vm.selectedSuggestionGroupIndex,
      onSelectedSuggestionGroupIndexChange: vm.setSelectedSuggestionGroupIndex,
      suggestionChooserOpen: vm.suggestionChooserOpen,
      onSuggestionChooserOpenChange: vm.setSuggestionChooserOpen,
      onSuggestionPick: vm.onSuggestionPick
    });

    return () => {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [
    resolvedUvInserts,
    setActiveUvInsertChooser,
    showChooser,
    slot.id,
    vm.activeConfig,
    vm.activeTabIndex,
    vm.onSuggestionPick,
    vm.optedIn,
    vm.selectedSuggestionGroupIndex,
    vm.setSelectedSuggestionGroupIndex,
    vm.setSuggestionChooserOpen,
    vm.setThemeChooserOpen,
    vm.setThemeIndex,
    vm.suggestionChooserOpen,
    vm.themeChooserOpen,
    vm.themeIndex
  ]);

  if (!resolvedUvInserts) return null;

  if (allowedConfigs.length === 0) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config not found for: {configHandles.join(', ')}. Check the slot configuration in admin.</p>
      </div>
    );
  }

  return (
    <div>
      {vm.isSlotRequired && <PersonalisationSlotHeader slot={slot} processHeading />}

      {vm.isSlotRequired && vm.effectivePrice !== null && (
        <p className={styles['personalisation__uv-insert-price'] + ' body-xs'}>
          {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}{' '}
          {vm.effectivePrice === 0 ? getZeroPriceLabel({ slot }) : `+${formatPrice({ amount: vm.effectivePrice })}`}
        </p>
      )}

      <div className={styles['personalisation__uv-insert']}>
        {!vm.isSlotRequired &&
          (() => {
            const title = slot.uvInsertPageId?.title || 'Add an insert';
            return (
              <ToggleInput
                id={`uv-insert-toggle-${slot.id}`}
                checked={vm.optedIn}
                onChange={vm.setOptedIn}
                description={slot.uvInsertPageId?.body}
                ariaLabel={togglePriceText ? `${title} ${togglePriceText}` : title}
                label={
                  <>
                    {title}
                    {togglePriceText && <span>&nbsp;{togglePriceText}</span>}
                  </>
                }
              />
            );
          })()}

        {vm.optedIn && (
          <>
            {showChooser ? (
              /* Step 1 - choose an insert type. */
              <div key="chooser" className={styles['personalisation__uv-insert-type-chooser']}>
                {allowedConfigs.map((config) => (
                  <button
                    key={config.handle}
                    type="button"
                    className={
                      styles['personalisation__uv-insert-type-option'] +
                      (config.handle === chosenHandle
                        ? ' ' + styles['personalisation__uv-insert-type-option--selected']
                        : '')
                    }
                    onClick={() => {
                      setChosenHandle(config.handle);
                      setShowChooser(false);
                    }}
                    aria-label={`Select ${config.title}`}
                    aria-selected={config.handle === chosenHandle}
                  >
                    {config.thumbnailSrc ? (
                      <img
                        src={config.thumbnailSrc}
                        className={styles['personalisation__uv-insert-type-option-thumbnail']}
                        width={48}
                        height={48}
                        aria-hidden="true"
                        alt=""
                      />
                    ) : (
                      <span
                        className={styles['personalisation__uv-insert-type-option-placeholder']}
                        aria-hidden="true"
                      />
                    )}
                    <div className={styles['personalisation__uv-insert-option-content']}>
                      {config.title && (
                        <p className={styles['personalisation__uv-insert-type-option-title'] + ' body-sm font-medium'}>
                          {config.title}
                        </p>
                      )}
                      {config.description && <p className="body-xs">{config.description}</p>}
                      {pricesVary && configMinPricePounds.has(config.handle) && (
                        <p
                          className={styles['personalisation__uv-insert-type-option-price'] + ' body-xs'}
                          aria-label="Additional price"
                        >
                          +{formatPrice({ amount: configMinPricePounds.get(config.handle) })}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              /* Step 2 — edit the chosen insert type. */
              <div key="editor" className={styles['personalisation__uv-insert-editor-wrapper']}>
                <div className={styles['personalisation__uv-insert-change-row']}>
                  <span className="body-sm">
                    {allowedConfigs.find((c) => c.handle === chosenHandle)?.title || chosenHandle}
                  </span>
                  <button
                    type="button"
                    aria-label="Change insert type"
                    className={styles['personalisation__uv-insert-change-btn'] + ' button-reset body-sm'}
                    onClick={() => setShowChooser(true)}
                  >
                    Change
                  </button>
                </div>
                <UvInsertEditorPlacement vm={vm} slot={slot} />
                <UvInsertPhotoUploads vm={vm} slot={slot} />
                <UvInsertFields vm={vm} slot={slot} cropFailedMessage={CROP_FAILED_MESSAGE} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Single allowed UV insert — full editor (unchanged from pre–multi-choice behaviour).
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @param {string} props.configHandle
 */
function PersonalisationUvInsertSlotSingle({ slot, configHandle }) {
  const {
    resolvedUvInserts,
    updateSelections,
    selections,
    setActiveUvInsertChooser,
    personalisationSteps,
    personalisationSlots
  } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  const existingSelection = /** @type {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} */ (
    selections?.slots.find((s) => s.id === slot.id)?.selected ?? null
  );

  const handleSelectionChange = useCallback(
    (selection) => {
      updateSelections({ slotId: slot.id, itemSelected: selection });
    },
    [slot.id, updateSelections]
  );

  const derivedTextByKey = useDerivedStoryCardText({ resolvedUvInserts, configHandle });

  // In a Story of Us (PAIRED) journey the footer's "Skip (No …)" is the opt-out for an optional
  // step, as in its Figma, so an optional insert on a step that has that Skip gets no toggle of its
  // own: it is included, with its heading shown in the step body, until the customer skips it. A
  // slot on a step without a Skip (a required step, or one no step lists) keeps its toggle.
  // The context holds the steps as a Map keyed by step id.
  const slotStep = [...(personalisationSteps?.values() ?? [])].find((step) => step?.slots?.includes(slot.id));
  const optOutInFooter = getStepSkipButtonText({ step: slotStep, personalisationSteps, personalisationSlots }) !== null;

  const vm = usePersonalisationUvInsertSlotViewModel({
    resolvedUvInserts,
    configHandle,
    isRequired: slot.isRequired,
    uvInsertVisibleByDefault: optOutInFooter || slot.uvInsertVisibleByDefault,
    isFreeSlot: slot.isFreeSlot,
    overridePrice: slot.overridePrice,
    existingSelection,
    derivedTextByKey,
    onSelectionChange: handleSelectionChange
  });

  usePublishCropPending({ slotId: slot.id, vm });

  useEffect(() => {
    if (!vm.optedIn || !vm.activeConfig || !resolvedUvInserts) {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setActiveUvInsertChooser({
      slotId: slot.id,
      config: vm.activeConfig,
      tabIndex: vm.activeTabIndex,
      suggestedMessaging: resolvedUvInserts.suggestedMessaging,
      selectedThemeIndex: vm.themeIndex,
      onThemeIndexChange: vm.setThemeIndex,
      themeChooserOpen: vm.themeChooserOpen,
      onThemeChooserOpenChange: vm.setThemeChooserOpen,
      selectedSuggestionGroupIndex: vm.selectedSuggestionGroupIndex,
      onSelectedSuggestionGroupIndexChange: vm.setSelectedSuggestionGroupIndex,
      suggestionChooserOpen: vm.suggestionChooserOpen,
      onSuggestionChooserOpenChange: vm.setSuggestionChooserOpen,
      onSuggestionPick: vm.onSuggestionPick
    });

    return () => {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [
    resolvedUvInserts,
    setActiveUvInsertChooser,
    slot.id,
    vm.activeConfig,
    vm.activeTabIndex,
    vm.onSuggestionPick,
    vm.optedIn,
    vm.selectedSuggestionGroupIndex,
    vm.setSelectedSuggestionGroupIndex,
    vm.setSuggestionChooserOpen,
    vm.setThemeChooserOpen,
    vm.setThemeIndex,
    vm.suggestionChooserOpen,
    vm.themeChooserOpen,
    vm.themeIndex
  ]);

  // Data not yet loaded from bootstrap — render nothing until context is ready.
  if (!resolvedUvInserts) return null;

  // The config handle on this slot doesn't match any resolved UV insert config.
  if (!vm.activeConfig) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config &ldquo;{configHandle}&rdquo; not found. Check the slot configuration.</p>
      </div>
    );
  }

  return (
    <div>
      {/*
        Required slots show the standard slot header (heading + optional help link).
        Optional slots use the ToggleInput as their heading row — showing the slot header
        separately would double-display uiHeading. With no toggle (the footer Skip is the opt-out)
        the header is shown too, without " (optional)" since the customer skips from the footer.
      */}
      {(vm.isSlotRequired || optOutInFooter) && (
        <PersonalisationSlotHeader slot={slot} processHeading={vm.isSlotRequired} />
      )}

      {/* Price display for required slots (shown below the slot header). */}
      {vm.isSlotRequired && vm.effectivePrice !== null && (
        <p className={styles['personalisation__uv-insert-price'] + ' body-xs'}>
          {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}{' '}
          {vm.effectivePrice === 0 ? getZeroPriceLabel({ slot }) : `+${formatPrice({ amount: vm.effectivePrice })}`}
        </p>
      )}
      <div className={styles['personalisation__uv-insert']}>
        {/* Optional slots: toggle with price inline in the label row. Required slots are always opted in. */}
        {!vm.isSlotRequired &&
          !optOutInFooter &&
          (() => {
            const title = slot.uvInsertPageId?.title || 'Add an insert';
            const priceText =
              vm.effectivePrice === null
                ? ''
                : vm.effectivePrice === 0
                  ? ` ${getZeroPriceLabel({ slot })}`
                  : ` +${formatPrice({ amount: vm.effectivePrice })}`;

            return (
              <ToggleInput
                id={`uv-insert-toggle-${slot.id}`}
                checked={vm.optedIn}
                onChange={vm.setOptedIn}
                description={slot.uvInsertPageId?.body}
                ariaLabel={`${title}${priceText}`}
                label={
                  <>
                    {title}
                    {vm.effectivePrice !== null && (
                      <span>
                        &nbsp;
                        {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}
                        {vm.effectivePrice === 0
                          ? ` ${getZeroPriceLabel({ slot })}`
                          : `+${formatPrice({ amount: vm.effectivePrice })}`}
                      </span>
                    )}
                  </>
                }
              />
            );
          })()}

        {/* Only render the insert UI when the user has opted in (or the slot is required). */}
        {vm.optedIn && (
          <>
            <UvInsertEditorPlacement vm={vm} slot={slot} />
            <UvInsertPhotoUploads vm={vm} slot={slot} />
            <UvInsertFields vm={vm} slot={slot} cropFailedMessage={CROP_FAILED_MESSAGE} />
          </>
        )}
      </div>
    </div>
  );
}
